import React, { useState, useEffect } from 'react';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import rolesService, { Role, Tab } from '../services/rolesService';
import './RoleManagement.css';

const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [availableTabs, setAvailableTabs] = useState<Tab[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Partial<Role> | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);

  useEffect(() => {
    fetchRoles();
    fetchAvailableTabs();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const rolesData = await rolesService.getAllRoles();
      setRoles(rolesData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch roles');
      console.error('Error fetching roles:', err);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTabs = async () => {
    try {
      const tabs = await rolesService.getAvailableTabs();
      setAvailableTabs(tabs);
    } catch (err) {
      console.error('Error fetching available tabs:', err);
      // Fallback to hardcoded tabs if API fails
      setAvailableTabs([
        { id: 'banner', name: 'Banner/Ticker', description: 'Manage banner and ticker content' },
        { id: 'ceo', name: 'CEO Message', description: 'Manage CEO message section' },
        { id: 'applications', name: 'Applications', description: 'Manage application links' },
        { id: 'image-banners', name: 'Image Banners', description: 'Manage image carousel banners' },
        { id: 'updates', name: 'JWIL Updates', description: 'Manage company updates' },
        { id: 'policies', name: 'Company Policies', description: 'Manage company policies' },
        { id: 'policy-categories', name: 'Policy Categories', description: 'Manage policy categories' },
        { id: 'e-procedures', name: 'E-Procedures', description: 'Manage e-procedures' },
        { id: 'procedure-categories', name: 'E-Procedure Categories', description: 'Manage e-procedure categories' },
        { id: 'snippets', name: 'Snippets', description: 'Manage content snippets' },
        { id: 'events', name: 'Events', description: 'Manage company events' },
        { id: 'leaders', name: 'Our Leaders', description: 'Manage leadership team' },
        { id: 'rewards', name: 'Rewards & Recognition', description: 'Manage rewards and recognition' },
        { id: 'users', name: 'User Management', description: 'Manage users (Super Admin only)' },
        { id: 'roles', name: 'Role Management', description: 'Manage roles (Super Admin only)' }
      ]);
    }
  };

  const handleAddClick = () => {
    setEditingRole({
      name: '',
      description: '',
      tabPermissions: [],
      isSystemRole: false
    });
    setIsModalOpen(true);
  };

  const handleEditClick = (role: Role) => {
    if (role.isSystemRole) {
      setError('Cannot edit system roles (Super Admin and Employee)');
      setTimeout(() => setError(null), 3000);
      return;
    }
    setEditingRole({
      id: role.id,
      name: role.name,
      description: role.description,
      tabPermissions: role.tabPermissions || [],
      isSystemRole: role.isSystemRole
    });
    setIsModalOpen(true);
  };

  const handleDeleteClick = (role: Role) => {
    if (role.isSystemRole) {
      setError('Cannot delete system roles (Super Admin and Employee)');
      setTimeout(() => setError(null), 3000);
      return;
    }
    setRoleToDelete(role);
    setShowDeleteModal(true);
  };

  const handleSave = async () => {
    if (!editingRole) return;

    try {
      const roleData = {
        name: editingRole.name!,
        description: editingRole.description!,
        tabPermissions: editingRole.tabPermissions || []
      };

      if (editingRole.id) {
        await rolesService.updateRole(editingRole.id, roleData);
        setSaveMessage('Role updated successfully');
      } else {
        await rolesService.createRole(roleData);
        setSaveMessage('Role created successfully');
      }

      setTimeout(() => setSaveMessage(''), 3000);
      setIsModalOpen(false);
      setEditingRole(null);
      fetchRoles();
    } catch (err: any) {
      setError(err.message || 'Failed to save role');
      console.error('Error saving role:', err);
    }
  };

  const handleDelete = async () => {
    if (!roleToDelete) return;

    try {
      await rolesService.deleteRole(roleToDelete.id);
      setSaveMessage('Role deleted successfully');
      setTimeout(() => setSaveMessage(''), 3000);
      setShowDeleteModal(false);
      setRoleToDelete(null);
      fetchRoles();
    } catch (err: any) {
      setError(err.message || 'Failed to delete role');
      console.error('Error deleting role:', err);
    }
  };

  const getTabNames = (tabPermissions: string[]): string => {
    if (!tabPermissions || tabPermissions.length === 0) return 'No access';
    if (tabPermissions.length === availableTabs.length) return 'All tabs';
    
    const tabNames = tabPermissions
      .map(tabId => availableTabs.find(t => t.id === tabId)?.name)
      .filter(Boolean)
      .slice(0, 3);
    
    const remaining = tabPermissions.length - 3;
    return tabNames.join(', ') + (remaining > 0 ? ` +${remaining} more` : '');
  };

  return (
    <div className="role-management-container">
      <div className="management-header">
        <h2>Role Management</h2>
        <button className="btn btn-primary" onClick={handleAddClick}>
          <i className="fas fa-plus"></i> Add Role
        </button>
      </div>

      {saveMessage && (
        <div className="alert alert-success" role="alert">
          {saveMessage}
        </div>
      )}

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="applications-table" style={{ marginTop: '20px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold' }}>Role Name</th>
                <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold' }}>Description</th>
                <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold' }}>Tab Access</th>
                <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '100px' }}>Type</th>
                <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '150px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id} style={{ borderBottom: '1px solid #ddd' }}>
                  <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                    <strong>{role.name}</strong>
                    {role.isSystemRole && (
                      <span style={{ 
                        marginLeft: '8px', 
                        padding: '2px 8px', 
                        backgroundColor: '#17a2b8', 
                        color: 'white', 
                        borderRadius: '4px', 
                        fontSize: '12px' 
                      }}>SYSTEM ROLE</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #ddd' }}>{role.description}</td>
                  <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                    <span className="tab-permissions-summary">
                      {getTabNames(role.tabPermissions)}
                    </span>
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>
                    {role.isSystemRole ? (
                      <span style={{ 
                        padding: '4px 12px', 
                        backgroundColor: '#6c757d', 
                        color: 'white', 
                        borderRadius: '4px', 
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>FIXED</span>
                    ) : (
                      <span style={{ 
                        padding: '4px 12px', 
                        backgroundColor: '#28a745', 
                        color: 'white', 
                        borderRadius: '4px', 
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>CUSTOM</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                      <button
                        onClick={() => handleEditClick(role)}
                        disabled={role.isSystemRole}
                        title={role.isSystemRole ? 'System roles cannot be edited' : 'Edit role'}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: role.isSystemRole ? 'not-allowed' : 'pointer',
                          padding: '6px 10px',
                          borderRadius: '4px',
                          fontSize: '16px',
                          transition: 'all 0.3s ease',
                          opacity: role.isSystemRole ? 0.3 : 1
                        }}
                        onMouseEnter={(e) => !role.isSystemRole && (e.currentTarget.style.background = '#e3f2fd')}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteClick(role)}
                        disabled={role.isSystemRole}
                        title={role.isSystemRole ? 'System roles cannot be deleted' : 'Delete role'}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: role.isSystemRole ? 'not-allowed' : 'pointer',
                          padding: '6px 10px',
                          borderRadius: '4px',
                          fontSize: '16px',
                          transition: 'all 0.3s ease',
                          opacity: role.isSystemRole ? 0.3 : 1
                        }}
                        onMouseEnter={(e) => !role.isSystemRole && (e.currentTarget.style.background = '#ffebee')}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Role Modal */}
      {isModalOpen && editingRole && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingRole.id ? 'Edit Role' : 'Add New Role'}</h3>
              <button className="close-btn" onClick={() => setIsModalOpen(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Role Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={editingRole.name || ''}
                  onChange={(e) => setEditingRole({ ...editingRole, name: e.target.value })}
                  placeholder="e.g., Content Manager"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  className="form-control"
                  value={editingRole.description || ''}
                  onChange={(e) => setEditingRole({ ...editingRole, description: e.target.value })}
                  placeholder="Describe the role's purpose"
                  rows={3}
                  required
                />
              </div>

              <div className="form-group">
                <label>Edit Content Tab Permissions</label>
                <p className="text-muted small">Select which Edit Content sections this role can access (hold Ctrl/Cmd to select multiple):</p>
                <select
                  multiple
                  className="form-control multi-select"
                  value={editingRole.tabPermissions || []}
                  onChange={(e) => {
                    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                    setEditingRole({
                      ...editingRole,
                      tabPermissions: selectedOptions
                    });
                  }}
                  size={10}
                  style={{ height: 'auto', minHeight: '200px' }}
                >
                  {availableTabs.map((tab) => (
                    <option key={tab.id} value={tab.id}>
                      {tab.name} - {tab.description}
                    </option>
                  ))}
                </select>
                <small className="text-muted">
                  Selected: {(editingRole.tabPermissions || []).length} tab(s)
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!editingRole.name || !editingRole.description}
              >
                {editingRole.id ? 'Update Role' : 'Add Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && roleToDelete && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          title="Delete Role"
          message={`Are you sure you want to delete the role "${roleToDelete.name}"? This action cannot be undone.`}
        />
      )}
    </div>
  );
};

export default RoleManagement;
